import { ChangeDetectorRef, Component, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexGrid,
  ApexLegend,
  ApexPlotOptions,
  ApexStates,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  ChartComponent
} from 'ng-apexcharts';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from 'core/services/auth.service';
import { CurrencyPipe } from '@angular/common';
import { BalanceService, IEarningsMonth } from 'core/services/balance.service';
import { HideApexChartTooltipOnScrollDirective } from 'shared/directives';
import { RefreshOnLanguageChangeDirective } from 'shared/directives/refresh-on-language-change.directive';
import { NO_PERSISTENT_CHART_SELECTION_STATES } from 'shared/types/chart.config';
import { DASHBOARD_EARNINGS_GROWTH_CHART_EVENTS } from '../dashboard-chart-interactions';
import { ResourceLoadErrorComponent } from 'shared/components/resource-load-error/resource-load-error.component';

export type CommunityMetricsChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  plotOptions: ApexPlotOptions;
  dataLabels: ApexDataLabels;
  colors: string[];
  grid: ApexGrid;
  tooltip: ApexTooltip;
  legend: ApexLegend;
  states: ApexStates;
};

@Component({
  selector: 'app-communitys-metrics',
  standalone: true,
  templateUrl: './communitys-metrics.component.html',
  styleUrls: ['./communitys-metrics.component.scss'],
  hostDirectives: [RefreshOnLanguageChangeDirective],
  imports: [
    TranslatePipe,
    ChartComponent,
    CurrencyPipe,
    HideApexChartTooltipOnScrollDirective,
    ResourceLoadErrorComponent
  ]
})
export class CommunitysMetricsComponent {
  #translateService = inject(TranslateService);
  #authService = inject(AuthService);
  #balanceService = inject(BalanceService);
  #changeDetectorRef = inject(ChangeDetectorRef);
  readonly #currentLanguage = toSignal(this.#translateService.onLangChange.pipe(map(event => event.lang)), {
    initialValue: this.#translateService.currentLang || this.#translateService.defaultLang || 'en'
  });

  constructor() {
    effect(() => {
      this.communityMetricsChartOptions();
      this.#changeDetectorRef.markForCheck();
    });
  }

  readonly dashboardStats = computed(() => this.#authService.currentUser?.dashboardStats);

  readonly invitationsSent = computed(() => this.dashboardStats()?.invitations_sent ?? 0);
  readonly avgCardSpending = computed(() => this.dashboardStats()?.avg_card_spending ?? 0);
  readonly newActivations = computed(() => this.dashboardStats()?.new_activations ?? 0);

  // Earnings growth graph data from API
  readonly earningsGrowthGraphResource = this.#balanceService.getEarningsGrowthGraph();
  readonly earningsGrowthGraphData = computed(() =>
    this.earningsGrowthGraphResource.hasValue() ? this.earningsGrowthGraphResource.value() : undefined
  );
  readonly earningsGrowthGraphLoading = computed(() => this.earningsGrowthGraphResource.isLoading());

  // Trend data from API
  readonly trendPercentage = computed(() => this.earningsGrowthGraphData()?.trend_percentage ?? 0);
  readonly trendDirection = computed(() => this.earningsGrowthGraphData()?.trend_direction ?? 'up');
  readonly isTrendUp = computed(() => this.trendDirection() === 'up');

  get currentLang(): string {
    return this.#currentLanguage();
  }

  readonly communityMetricsChartOptions = computed<CommunityMetricsChartOptions>(() => {
    this.#currentLanguage();
    const earningsData = this.earningsGrowthGraphData();
    const months = earningsData?.months || [];
    const monthSlots = this.getLastSixMonths(months);

    return {
      series: [
        {
          name: this.#translateService.instant('myGanarbols.earningGrowth'),
          data: monthSlots.map(month => month.earnings)
        }
      ],
      chart: {
        type: 'bar',
        height: 280,
        toolbar: { show: false },
        zoom: { enabled: false },
        events: DASHBOARD_EARNINGS_GROWTH_CHART_EVENTS
      },
      xaxis: {
        categories: monthSlots.map(month => month.label),
        crosshairs: {
          show: true,
          position: 'back',
          width: 'tickWidth',
          fill: {
            type: 'solid',
            color: '#E6F2FA',
            opacity: 0.45
          },
          stroke: {
            width: 0
          }
        },
        labels: {
          style: {
            colors: '#737980',
            fontSize: '12px',
            fontFamily: 'Outfit, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }
        }
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        decimalsInFloat: 0,
        labels: {
          formatter: (value: number) => Math.round(value).toString(),
          style: {
            colors: '#737980',
            fontSize: '12px',
            fontFamily: 'Outfit, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }
        }
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          columnWidth: '60%'
        }
      },
      dataLabels: {
        enabled: false
      },
      colors: ['#007DCB'],
      grid: {
        strokeDashArray: 4,
        borderColor: '#EEEEEF',
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } }
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        followCursor: true,
        hideEmptySeries: false,
        theme: 'light'
      },
      legend: {
        show: false
      },
      states: NO_PERSISTENT_CHART_SELECTION_STATES
    };
  });

  onGrowYourCommunityClick(): void {
    // TODO: Implement navigation to grow community page
  }

  private getLastSixMonths(months: IEarningsMonth[]): Array<{ label: string; earnings: number }> {
    const formatter = new Intl.DateTimeFormat(this.currentLang === 'es' ? 'es-ES' : 'en-US', { month: 'short' });
    const now = new Date();
    const earningsByMonth = new Map(months.map(month => [`${month.year}-${month.month_number}`, month]));

    return Array.from({ length: 6 }, (_, index) => {
      const date = new Date(now.getFullYear(), now.getMonth() - 5 + index, 1);
      const earningsMonth = earningsByMonth.get(`${date.getFullYear()}-${date.getMonth() + 1}`);
      const label = formatter.format(date).replace('.', '');

      return {
        label: label.charAt(0).toUpperCase() + label.slice(1),
        earnings: earningsMonth?.earnings ?? 0
      };
    });
  }
}
